// src/components/ArtifactReviewPanel.jsx
// P2b: Human Review UI for AI Governance

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Button,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Tooltip,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CheckCircle as ApprovedIcon,
  Edit as DraftIcon,
  Cancel as RejectedIcon,
  RateReview as ReviewIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

// Status configuration
const STATUS_CONFIG = {
  approved: {
    label: 'Approved',
    color: 'success',
    icon: <ApprovedIcon fontSize="small" />
  },
  draft: {
    label: 'Draft',
    color: 'warning',
    icon: <DraftIcon fontSize="small" />
  },
  rejected: {
    label: 'Rejected',
    color: 'error',
    icon: <RejectedIcon fontSize="small" />
  },
  pending: {
    label: 'Pending Review',
    color: 'default',
    icon: <ReviewIcon fontSize="small" />
  }
};

/**
 * ArtifactReviewPanel - Displays review status and allows marking artifacts as reviewed
 * 
 * @param {string} artifactType - 'businessUpdate' | 'question'
 * @param {string} artifactId - Document ID (or parent doc ID for followUps)
 * @param {string} currentStatus - Current review status: 'draft' | 'approved' | 'rejected' | null
 * @param {string} reviewedByEmail - Email of last reviewer
 * @param {object} reviewedAt - Timestamp of last review
 * @param {number} followUpIndex - Required for Decision Memos in followUps array (optional)
 * @param {function} onReviewComplete - Callback after successful review (optional)
 * @param {boolean} compact - If true, shows minimal UI (just chip + button)
 * @param {boolean} disabled - Disable review actions
 */
const ArtifactReviewPanel = ({
  artifactType,
  artifactId,
  currentStatus,
  reviewedByEmail,
  reviewedAt,
  reviewNotes,
  reviewHistory = [],
  followUpIndex,
  onReviewComplete,
  compact = false,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [notes, setNotes] = useState('');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [clarificationDialogOpen, setClarificationDialogOpen] = useState(false);
  const [clarificationNotes, setClarificationNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const menuOpen = Boolean(anchorEl);

  // Get display config for current status
  const statusConfig = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.pending;

  // Format timestamp
  const formatReviewDate = (timestamp) => {
    if (!timestamp) return null;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle menu open
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle status selection - all status changes require notes for SOC 2 audit trail
  const handleStatusSelect = (status) => {
    handleMenuClose();
    setSelectedStatus(status);
    setNotes('');
    setNotesDialogOpen(true);
  };

  // Submit review to Cloud Function
  const submitReview = async (status, reviewNotes) => {
    setSubmitting(true);
    
    try {
      const functions = getFunctions();
      const recordReview = httpsCallable(functions, 'recordArtifactReview');
      
      const payload = {
        artifactType,
        artifactId,
        status,
        notes: reviewNotes || null
      };

      // Include followUpIndex for Decision Memos
      if (typeof followUpIndex === 'number') {
        payload.followUpIndex = followUpIndex;
      }

      const result = await recordReview(payload);

      if (result.data.success) {
        toast.success(`Marked as ${STATUS_CONFIG[status].label}`);
        setNotesDialogOpen(false);
        setNotes('');
        setSelectedStatus(null);
        
        if (onReviewComplete) {
          onReviewComplete(status);
        }
      } else {
        throw new Error(result.data.error || 'Failed to record review');
      }
    } catch (error) {
      console.error('[REVIEW] Error submitting review:', error);
      toast.error(`Failed to record review: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle notes dialog submit
  const handleNotesSubmit = () => {
    if (selectedStatus) {
      submitReview(selectedStatus, notes);
    }
  };

  // P4: Submit clarification (no status change)
  const submitClarification = async () => {
    if (clarificationNotes.trim().length < 5) return;
    
    setSubmitting(true);
    
    try {
      const functions = getFunctions();
      const recordReview = httpsCallable(functions, 'recordArtifactReview');
      
      const payload = {
        artifactType,
        artifactId,
        clarification: true,
        notes: clarificationNotes.trim()
      };

      // Include followUpIndex for Decision Memos
      if (typeof followUpIndex === 'number') {
        payload.followUpIndex = followUpIndex;
      }

      const result = await recordReview(payload);

      if (result.data.success) {
        toast.success('Clarification added to audit trail');
        setClarificationDialogOpen(false);
        setClarificationNotes('');
        setHistoryExpanded(true); // Auto-expand to show new clarification
        
        if (onReviewComplete) {
          onReviewComplete('clarification');
        }
      } else {
        throw new Error(result.data.error || 'Failed to add clarification');
      }
    } catch (error) {
      console.error('[REVIEW] Error adding clarification:', error);
      toast.error(`Failed to add clarification: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Compact view - just status chip and action button
  if (compact) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Row 1: Status chip + Review button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            size="small"
            color={statusConfig.color}
            variant="outlined"
          />
          <Tooltip title="Set Review Status">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={handleMenuOpen}
                disabled={disabled || submitting}
                startIcon={submitting ? <CircularProgress size={14} /> : <ReviewIcon />}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Review
              </Button>
            </span>
          </Tooltip>
          {/* P4: Add Clarification button - only show if already reviewed */}
          {currentStatus && (
            <Tooltip title="Add clarification note without changing status">
              <span>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setClarificationDialogOpen(true)}
                  disabled={disabled || submitting}
                  sx={{ minWidth: 'auto', px: 1, color: '#6366f1' }}
                >
                  + Review Note
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* P1: Display review notes if present */}
      {reviewNotes && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Review Notes:
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {reviewNotes}
          </Typography>
        </Box>
      )}

      {/* P3: Expandable Review History */}
      {reviewHistory && reviewHistory.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setHistoryExpanded(!historyExpanded)}
            startIcon={<HistoryIcon fontSize="small" />}
            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
          >
            {historyExpanded ? '▼' : '▶'} View History ({reviewHistory.length})
          </Button>
          
          {historyExpanded && (
            <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid #e0e0e0' }}>
              {[...reviewHistory].reverse().map((entry, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    mb: 1.5, 
                    pb: 1.5, 
                    borderBottom: index < reviewHistory.length - 1 ? '1px solid #f0f0f0' : 'none' 
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {entry.timestamp?.toDate ? 
                      entry.timestamp.toDate().toLocaleString() : 
                      new Date(entry.timestamp).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {entry.action === 'status_set' ? (
                      <span style={{ color: '#10b981' }}>● Initial Review</span>
                    ) : entry.action === 'status_changed' ? (
                      <span style={{ color: '#f59e0b' }}>↻ Status Changed</span>
                    ) : (
                      <span style={{ color: '#6366f1' }}>✎ Clarification Added</span>
                    )}
                    {entry.newStatus && (
                      <>
                        {': '}
                        {entry.previousStatus && (
                          <span style={{ textTransform: 'capitalize' }}>
                            {entry.previousStatus} → 
                          </span>
                        )}
                        <span style={{ textTransform: 'capitalize' }}>{entry.newStatus}</span>
                      </>
                    )}
                  </Typography>
                  {entry.notes && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                      "{entry.notes}"
                    </Typography>
                  )}
                  {entry.userEmail && (
                    <Typography variant="caption" color="text.secondary">
                      by {entry.userEmail}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Status Selection Menu */}
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleStatusSelect('approved')} disabled={currentStatus === 'approved'}>
            <ListItemIcon><ApprovedIcon color="success" /></ListItemIcon>
            <ListItemText>Approve</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleStatusSelect('draft')} disabled={currentStatus === 'draft'}>
            <ListItemIcon><DraftIcon color="warning" /></ListItemIcon>
            <ListItemText>Mark as Draft</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleStatusSelect('rejected')} disabled={currentStatus === 'rejected'}>
            <ListItemIcon><RejectedIcon color="error" /></ListItemIcon>
            <ListItemText>Reject</ListItemText>
          </MenuItem>
        </Menu>

        {/* Notes Dialog */}
        <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
          {selectedStatus === 'approved' ? 'Approve Content' : selectedStatus === 'draft' ? 'Mark as Draft' : 'Reject Content'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add optional notes for the audit trail:
            </Typography>
            <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            required
            label="Review Notes (Required)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Verified against Q/B data, Ready for client delivery..."
            error={notes.trim().length > 0 && notes.trim().length < 5}
            helperText={
              notes.trim().length === 0 
                ? "Document your review decision for the audit trail (minimum 5 characters)"
                : notes.trim().length < 5
                  ? `${5 - notes.trim().length} more characters required`
                  : "This note will be recorded in the audit trail"
            }
          />
          </DialogContent>
          <DialogActions>
            <Button
            onClick={handleNotesSubmit}
            variant="contained"
                        color={selectedStatus === 'approved' ? 'success' : selectedStatus === 'draft' ? 'warning' : 'error'}
            disabled={submitting || notes.trim().length < 5}
          >
                        {submitting ? <CircularProgress size={20} /> : `Confirm ${selectedStatus === 'approved' ? 'Approval' : selectedStatus === 'draft' ? 'Draft' : 'Rejection'}`}
          </Button>
          </DialogActions>
        </Dialog>

        {/* P4: Clarification Dialog */}
        <Dialog open={clarificationDialogOpen} onClose={() => setClarificationDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Clarification Note</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a note to the audit trail without changing the review status.
            </Typography>
            <TextField
              autoFocus
              multiline
              rows={3}
              fullWidth
              required
              label="Clarification Note (Required)"
              value={clarificationNotes}
              onChange={(e) => setClarificationNotes(e.target.value)}
              placeholder="e.g., Updated source data verified, Client confirmed accuracy..."
              error={clarificationNotes.trim().length > 0 && clarificationNotes.trim().length < 5}
              helperText={
                clarificationNotes.trim().length === 0 
                  ? "Add context or follow-up notes (minimum 5 characters)"
                  : clarificationNotes.trim().length < 5
                    ? `${5 - clarificationNotes.trim().length} more characters required`
                    : "This note will be recorded in the audit trail"
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClarificationDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submitClarification}
              variant="contained"
              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
              disabled={submitting || clarificationNotes.trim().length < 5}
            >
              {submitting ? <CircularProgress size={20} /> : 'Add Clarification'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Full view - includes reviewer info and history
  return (
    <Box sx={{ 
      p: 2, 
      bgcolor: 'grey.50', 
      borderRadius: 1, 
      border: '1px solid',
      borderColor: 'grey.200'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Review Status
          </Typography>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            size="medium"
            color={statusConfig.color}
            variant="filled"
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleMenuOpen}
            disabled={disabled || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <ReviewIcon />}
          >
            {currentStatus ? 'Update Review' : 'Mark Reviewed'}
          </Button>
          {/* P4: Add Clarification button - only show if already reviewed */}
          {currentStatus && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setClarificationDialogOpen(true)}
              disabled={disabled || submitting}
              sx={{ color: '#6366f1', borderColor: '#6366f1' }}
            >
              + Review Note
            </Button>
          )}
        </Box>
      </Box>

      {/* Reviewer info */}
      {reviewedByEmail && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Last reviewed by {reviewedByEmail}
          {reviewedAt && ` on ${formatReviewDate(reviewedAt)}`}
        </Typography>
      )}

      {/* P1: Display review notes if present */}
      {reviewNotes && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Review Notes:
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {reviewNotes}
          </Typography>
        </Box>
      )}

      {/* P3: Expandable Review History */}
      {reviewHistory && reviewHistory.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setHistoryExpanded(!historyExpanded)}
            startIcon={<HistoryIcon fontSize="small" />}
            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
          >
            {historyExpanded ? '▼' : '▶'} View History ({reviewHistory.length})
          </Button>
          
          {historyExpanded && (
            <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid #e0e0e0' }}>
              {[...reviewHistory].reverse().map((entry, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    mb: 1.5, 
                    pb: 1.5, 
                    borderBottom: index < reviewHistory.length - 1 ? '1px solid #f0f0f0' : 'none' 
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {entry.timestamp?.toDate ? 
                      entry.timestamp.toDate().toLocaleString() : 
                      new Date(entry.timestamp).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {entry.action === 'status_set' ? (
                      <span style={{ color: '#10b981' }}>● Initial Review</span>
                    ) : entry.action === 'status_changed' ? (
                      <span style={{ color: '#f59e0b' }}>↻ Status Changed</span>
                    ) : (
                      <span style={{ color: '#6366f1' }}>✎ Clarification Added</span>
                    )}
                    {entry.newStatus && (
                      <>
                        {': '}
                        {entry.previousStatus && (
                          <span style={{ textTransform: 'capitalize' }}>
                            {entry.previousStatus} → 
                          </span>
                        )}
                        <span style={{ textTransform: 'capitalize' }}>{entry.newStatus}</span>
                      </>
                    )}
                  </Typography>
                  {entry.notes && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                      "{entry.notes}"
                    </Typography>
                  )}
                  {entry.userEmail && (
                    <Typography variant="caption" color="text.secondary">
                      by {entry.userEmail}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Status Selection Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleStatusSelect('approved')} disabled={currentStatus === 'approved'}>
          <ListItemIcon><ApprovedIcon color="success" /></ListItemIcon>
          <ListItemText primary="Approve" secondary="Ready for client delivery" />
        </MenuItem>
        <MenuItem onClick={() => handleStatusSelect('draft')} disabled={currentStatus === 'draft'}>
          <ListItemIcon><DraftIcon color="warning" /></ListItemIcon>
          <ListItemText primary="Mark as Draft" secondary="Needs further review" />
        </MenuItem>
        <MenuItem onClick={() => handleStatusSelect('rejected')} disabled={currentStatus === 'rejected'}>
          <ListItemIcon><RejectedIcon color="error" /></ListItemIcon>
          <ListItemText primary="Reject" secondary="Do not use this content" />
        </MenuItem>
      </Menu>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedStatus === 'approved' ? 'Approve Content' : 'Reject Content'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add optional notes for the audit trail:
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            required
            label="Review Notes (Required)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Verified against Q/B data, Ready for client delivery..."
            error={notes.trim().length > 0 && notes.trim().length < 5}
            helperText={
              notes.trim().length === 0 
                ? "Document your review decision for the audit trail (minimum 5 characters)"
                : notes.trim().length < 5
                  ? `${5 - notes.trim().length} more characters required`
                  : "This note will be recorded in the audit trail"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleNotesSubmit}
            variant="contained"
            color={selectedStatus === 'approved' ? 'success' : 'error'}
            disabled={submitting || notes.trim().length < 5}
          >
            {submitting ? <CircularProgress size={20} /> : `Confirm ${selectedStatus === 'approved' ? 'Approval' : 'Rejection'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* P4: Clarification Dialog */}
      <Dialog open={clarificationDialogOpen} onClose={() => setClarificationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Clarification Note</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a note to the audit trail without changing the review status.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            required
            label="Clarification Note (Required)"
            value={clarificationNotes}
            onChange={(e) => setClarificationNotes(e.target.value)}
            placeholder="e.g., Updated source data verified, Client confirmed accuracy..."
            error={clarificationNotes.trim().length > 0 && clarificationNotes.trim().length < 5}
            helperText={
              clarificationNotes.trim().length === 0 
                ? "Add context or follow-up notes (minimum 5 characters)"
                : clarificationNotes.trim().length < 5
                  ? `${5 - clarificationNotes.trim().length} more characters required`
                  : "This note will be recorded in the audit trail"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClarificationDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={submitClarification}
            variant="contained"
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
            disabled={submitting || clarificationNotes.trim().length < 5}
          >
            {submitting ? <CircularProgress size={20} /> : 'Add Clarification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArtifactReviewPanel;